import EntityManager from "@/components/EntityManager";
import { useLocation } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BRANCHES } from "@/lib/branches";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HEAD_OFFICE } from "@/lib/company";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, BookOpenText, Download, BarChart3, MessageCircle, Paperclip, Eye } from "lucide-react";
import { inr } from "@/lib/format";
import { fmtDate, today } from "@/lib/format";
import { printDebitVoucher, printDebitVouchers, printVoucherRegister, printVoucherSummary, printVendorPaymentSummary } from "@/lib/print";
import { logPadIssue, PadLogButton } from "@/components/PadIssueLog";
import { toast } from "sonner";

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const MODES = ["Cash", "Bank", "UPI", "Cheque", "NEFT", "RTGS", "DD", "IMPS", "Card"];
const CATEGORIES = [
  "Fuel",
  "Petrol",
  "Driver Payment",
  "Vehicle Maintenance",
  "Vehicle Hire",
  "Vehicle Advance",
  "Vehicle Balance",
  "Loading Charge",
  "Unloading Charge",
  "Tolls & Parking",
  "Office & Admin",
  "Office Expense",
  "Stationary",
  "Loading/Unloading",
  "Supplier Payment",
  "General",
];

const PROOF_ALERT_LIMIT = 5000;

const voucherProofUrl = (r) => `${API}/files/${r.proof_path}?auth=${localStorage.getItem("ntc_token")}`;

function VoucherProofActions({ row }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/vouchers/${row.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill photo attached");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`voucher-proof-file-input-${row.id}`} />
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 ${row.proof_path ? "text-emerald-600" : "text-slate-400"}`}
        title={row.proof_path ? "Replace bill photo" : "Attach bill photo"}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid="attach-voucher-proof-btn"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </>
  );
}

function BlankVoucherDialog({ open, onClose, existingRows }) {
  const [startNo, setStartNo] = useState("");
  const [count, setCount] = useState(1);
  const [clashes, setClashes] = useState([]);
  const [warned, setWarned] = useState(false);
  useEffect(() => { if (open) { setStartNo(""); setCount(1); setClashes([]); setWarned(false); } }, [open]);
  const go = () => {
    const n = Math.min(Math.max(Number(count) || 1, 1), 50);
    const m = String(startNo).trim().match(/^(.*?)(\d+)$/);
    const list = Array.from({ length: n }, (_, i) => {
      if (!startNo.trim()) return { blank: true };
      if (!m || n === 1) return { blank: true, voucher_no: startNo.trim() };
      return { blank: true, voucher_no: m[1] + String(Number(m[2]) + i).padStart(m[2].length, "0") };
    });
    const norm = (v) => String(v || "").replace(/^DV[-\s]?/i, "").trim().toLowerCase();
    const used = new Set((existingRows || []).map((r) => norm(r.voucher_no)).filter(Boolean));
    const bad = list.map((l) => l.voucher_no).filter((no) => no && used.has(norm(no)));
    if (bad.length && !warned) {
      setClashes(bad);
      setWarned(true);
      return;
    }
    printDebitVouchers(list);
    logPadIssue("Voucher", list, "voucher_no");
    onClose();
  };
  const onChange = (fn) => (e) => { fn(e.target.value); setWarned(false); setClashes([]); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-testid="blank-voucher-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Blank Voucher Print</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Starting Voucher No. (manual entry)</Label>
            <Input value={startNo} onChange={onChange(setStartNo)} placeholder="e.g. 3001 (leave blank for no number)" data-testid="blank-voucher-start-no" />
          </div>
          <div className="grid gap-1.5">
            <Label>How many blank vouchers</Label>
            <Input type="number" min="1" max="50" value={count} onChange={onChange(setCount)} data-testid="blank-voucher-count" />
            <p className="text-xs text-muted-foreground">Serial numbers auto-increment from the starting Voucher No. and get recorded in the Pad Log.</p>
          </div>
          {clashes.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="blank-voucher-clash-warning">
              ⚠ Voucher No. {clashes.join(", ")} already exist{clashes.length === 1 ? "s" : ""} in the system. Click "Print Anyway" to continue or change the number.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant={clashes.length ? "destructive" : "default"} onClick={go} data-testid="blank-voucher-continue-btn">
            <Printer className="mr-1.5 h-4 w-4" /> {clashes.length ? "Print Anyway" : "Print"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const voucherWhatsApp = (v) => {
  const lines = [
    `🧾 *NTC Debit Voucher ${String(v.voucher_no || "").replace(/^DV[-\s]?/i, "")}*`,
    `Date: ${fmtDate(v.date)} | Branch: ${v.branch || "Chennai"}`,
    `Paid To: ${v.party_name || "—"}`,
    `Category: ${v.category || "—"}`,
    `Mode: ${v.payment_mode || "Cash"}${v.cheque_no ? ` (Cheque ${v.cheque_no})` : ""}`,
    `Amount: ₹${Number(v.amount || 0).toLocaleString("en-IN")}`,
    v.narration ? `For: ${v.narration}` : "",
    "",
    "— Nisha Transport Corporation",
  ].filter((l) => l !== "");
  window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
};

function VoucherSummary({ rows }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(today().slice(0, 7));
  const list = useMemo(() => {
    const m = {};
    (rows || [])
      .filter((v) => String(v.date || String(v.created_at || "").slice(0, 10)).startsWith(month))
      .forEach((v) => {
        const c = v.category || "General";
        const a = (m[c] = m[c] || { category: c, count: 0, total: 0 });
        a.count += 1;
        a.total += Number(v.amount) || 0;
      });
    return Object.values(m).sort((x, y) => y.total - x.total);
  }, [rows, month]);
  const grand = list.reduce((a, c) => a + c.total, 0);
  const monthLabel = new Date(`${month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <>
      <Button size="sm" variant="outline" className="bg-white" onClick={() => setOpen(true)} data-testid="voucher-summary-btn">
        <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Monthly Summary
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle className="font-display">Voucher Expense Summary — by Category</DialogTitle></DialogHeader>
          <div className="flex items-end gap-3">
            <Input type="month" className="h-9 w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="voucher-summary-month" />
            <Button variant="outline" className="h-9" disabled={!list.length} onClick={() => printVoucherSummary(monthLabel, list)} data-testid="voucher-summary-print">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Category", "Vouchers", "Total"].map((h, i) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.12em] text-slate-500 ${i > 0 ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No vouchers for {monthLabel}.</TableCell></TableRow>}
                {list.map((c) => (
                  <TableRow key={c.category} data-testid="voucher-summary-row">
                    <TableCell className="text-sm font-medium">{c.category}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{c.count}</TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(c.total)}</TableCell>
                  </TableRow>
                ))}
                {list.length > 0 && (
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableCell className="text-sm font-bold">TOTAL</TableCell>
                    <TableCell className="text-right text-sm font-bold tabular-nums">{list.reduce((a, c) => a + c.count, 0)}</TableCell>
                    <TableCell className="text-right text-sm font-bold tabular-nums">{inr(grand)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function VendorSummary() {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(today().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open) api.get(`/vouchers/vendor-summary?month=${month}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], grand_total: 0 }));
  }, [open, month]);
  const list = data?.rows || [];
  const monthLabel = new Date(`${month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <>
      <Button size="sm" variant="outline" className="bg-white" onClick={() => setOpen(true)} data-testid="vendor-summary-btn">
        <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Vendor Payments
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[640px]" data-testid="vendor-summary-dialog">
          <DialogHeader><DialogTitle className="font-display">Vendor / Broker Payments — {monthLabel}</DialogTitle></DialogHeader>
          <div className="flex items-end gap-3">
            <Input type="month" className="h-9 w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="vendor-summary-month" />
            <Button variant="outline" className="h-9" disabled={!list.length} onClick={() => printVendorPaymentSummary(monthLabel, list)} data-testid="vendor-summary-print">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Vendor / Broker", "Code", "Vouchers", "Modes", "Total Paid"].map((h, i) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.12em] text-slate-500 ${i >= 2 ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>}
                {data && list.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No vouchers for {monthLabel}.</TableCell></TableRow>}
                {list.map((v) => (
                  <TableRow key={v.vendor} data-testid="vendor-summary-row">
                    <TableCell className="text-sm font-medium">{v.vendor}</TableCell>
                    <TableCell className="text-sm">{v.code || "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{v.count}</TableCell>
                    <TableCell className="text-right text-xs text-slate-500">{(v.modes || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(v.total)}</TableCell>
                  </TableRow>
                ))}
                {list.length > 0 && (
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableCell className="text-sm font-bold" colSpan={2}>TOTAL</TableCell>
                    <TableCell className="text-right text-sm font-bold tabular-nums">{list.reduce((a, c) => a + c.count, 0)}</TableCell>
                    <TableCell />
                    <TableCell className="text-right text-sm font-bold tabular-nums">{inr(data?.grand_total || 0)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function DebitVouchers() {
  const { user } = useAuth();
  const location = useLocation();
  const payParams = new URLSearchParams(location.search);
  const payTo = payParams.get("pay_to") || "";
  const initialCreate = payTo
    ? { party_name: payTo, amount: Number(payParams.get("amount")) || 0, category: "Vehicle Hire", narration: `Lorry hire settlement — ${payTo}${payParams.get("code") ? ` (${payParams.get("code")})` : ""}` }
    : undefined;
  const [regFrom, setRegFrom] = useState(firstOfMonth());
  const [regTo, setRegTo] = useState(today());
  const [printing, setPrinting] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [proofLimit, setProofLimit] = useState(PROOF_ALERT_LIMIT);
  const [blankOpen, setBlankOpen] = useState(false);

  const refreshRows = () => api.get("/vouchers").then((r) => setAllRows(r.data || [])).catch(() => {});
  useEffect(() => {
    refreshRows();
    api.get("/app-settings").then((r) => setProofLimit(Number(r.data?.proof_alert_limit) || PROOF_ALERT_LIMIT)).catch(() => {});
  }, []);

  const manualSuggestions = useMemo(() => {
    const used = new Set(allRows.map((v) => v.category).filter(Boolean));
    return [...used].filter((c) => !CATEGORIES.includes(c)).sort();
  }, [allRows]);

  const getRangeRows = async () => {
    const res = await api.get("/vouchers");
    const rows = (res.data || []).filter((v) => {
      const d = (v.date || String(v.created_at || "").slice(0, 10)) || "";
      return (!regFrom || d >= regFrom) && (!regTo || d <= regTo);
    });
    rows.sort((a, b) => ((a.date || "") < (b.date || "") ? -1 : 1));
    return rows;
  };

  const printRegister = async () => {
    setPrinting(true);
    try {
      const rows = await getRangeRows();
      if (!rows.length) {
        toast.info("No vouchers in the selected date range");
        return;
      }
      printVoucherRegister(rows, { from: regFrom, to: regTo });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPrinting(false);
    }
  };

  const exportExcel = async () => {
    try {
      const rows = await getRangeRows();
      if (!rows.length) {
        toast.info("No vouchers in the selected date range");
        return;
      }
      const total = rows.reduce((s, v) => s + (Number(v.amount) || 0), 0);
      const csv = [
        "Sl,Voucher No.,Date,Branch,Paid To,A/c,Category,Mode,Cheque/Ref,Narration,Amount",
        ...rows.map((v, i) =>
          [i + 1, String(v.voucher_no || "").replace(/^DV[-\s]?/i, ""), v.date || "", v.branch || "Chennai", v.party_name, v.account_no, v.category, v.payment_mode || "Cash", v.cheque_no || v.reference || "", v.narration, v.amount]
            .map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
        `"","","","","","","","","","TOTAL","${total}"`,
      ].join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `voucher-register-${regFrom || "all"}_${regTo || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} vouchers`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="voucher-register-bar">
        <div className="flex items-center gap-2 pb-2 text-sm font-semibold">
          <BookOpenText className="h-4 w-4 text-primary" /> Voucher Register
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-9 w-[140px] bg-white" value={regFrom} onChange={(e) => setRegFrom(e.target.value)} data-testid="register-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[140px] bg-white" value={regTo} onChange={(e) => setRegTo(e.target.value)} data-testid="register-to" />
        </div>
        <Button size="sm" onClick={printRegister} disabled={printing} data-testid="print-register-btn">
          <Printer className="mr-1.5 h-3.5 w-3.5" /> {printing ? "Preparing…" : "Print Register"}
        </Button>
        <Button size="sm" variant="outline" className="bg-white" onClick={exportExcel} data-testid="export-register-btn">
          <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
        {user?.role === "admin" && <VoucherSummary rows={allRows} />}
        {user?.role === "admin" && <VendorSummary />}
        <Button size="sm" variant="outline" className="bg-white" onClick={() => setBlankOpen(true)} data-testid="blank-voucher-print-btn">
          <Printer className="mr-1.5 h-3.5 w-3.5" /> Blank Print
        </Button>
        <PadLogButton />
      </div>
    <EntityManager
      transformBeforeSave={(f) => {
        const { category_manual, ...rest } = f;
        return category_manual && category_manual.trim() ? { ...rest, category: category_manual.trim() } : rest;
      }}
      title="Debit Vouchers"
      subtitle="Record cash/bank payments made by NTC (money going out). Print a voucher in the official NTC format."
      endpoint="vouchers"
      entityName="Voucher"
      selectable
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Payee / voucher no / category / mode",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [String(r.voucher_no || "").replace(/^DV[-\s]?/i, ""), r.party_name, r.category, r.payment_mode, r.branch, r.narration, r.account_no, r.cheque_no, r.bank_name, r.amount]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
      ]}
      onPrintSelected={(list) => printVoucherRegister(list, {})}
      initialCreate={initialCreate}
      defaults={{
        party_name: "",
        amount: 0,
        payment_mode: "Cash",
        category: "General",
        category_manual: "",
        narration: "",
        reference: "",
        branch: "",
        account_no: "",
        cheque_no: "",
        cheque_date: "",
        cheque_amount: 0,
        transaction_date: "",
        bank_name: "",
        date: today(),
      }}
      fields={[
        { key: "branch", label: "Branch", type: "select", options: BRANCHES },
        { key: "party_name", label: "To (party / payee)", type: "text", placeholder: "HP Petrol Pump" },
        { key: "account_no", label: "A/c (account)", type: "text", placeholder: "Account / ledger" },
        { key: "amount", label: "Amount (₹)", type: "number", placeholder: "12000" },
        { key: "payment_mode", label: "Payment mode", type: "select", options: MODES },
        { key: "category", label: "Category", type: "select", options: CATEGORIES },
        { key: "category_manual", label: "Category (manual entry)", type: "text", placeholder: "Type other expense e.g. Toll, Tea", suggestions: manualSuggestions },
        { key: "cheque_no", label: "Cheque No.", type: "text", placeholder: "e.g. 123456", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "cheque_date", label: "Cheque Date", type: "date", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "cheque_amount", label: "Cheque Amount", type: "number", placeholder: "0", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "bank_name", label: "Bank Name", type: "text", placeholder: "e.g. Punjab National Bank", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "transaction_date", label: "Transaction date", type: "date" },
        { key: "narration", label: "Being the amount paid for (narration)", type: "textarea", placeholder: "Purpose of payment" },
        { key: "date", label: "Voucher date", type: "date" },
      ]}
      columns={[
        { key: "sl_no", label: "Sl No.", render: (r, i) => i + 1 },
        { key: "branch", label: "Branch", render: (r) => r.branch || "Chennai" },
        { key: "voucher_no", label: "Voucher No.", render: (r) => String(r.voucher_no || "").replace(/^DV[-\s]?/i, "") || "—" },
        { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
        { key: "transaction_date", label: "Transaction Date", render: (r) => (r.transaction_date ? fmtDate(r.transaction_date) : "—") },
        { key: "party_name", label: "Paid To" },
        { key: "category", label: "Category" },
        { key: "payment_mode", label: "Payment Mode" },
        {
          key: "proof",
          label: "Bill Proof",
          render: (r) =>
            r.proof_path ? (
              <a
                href={voucherProofUrl(r)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"
                title={r.proof_filename || "View bill photo"}
                data-testid="view-voucher-proof-link"
              >
                <Eye className="h-3.5 w-3.5" /> View
              </a>
            ) : Number(r.amount) >= proofLimit ? (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" title={`Amount ≥ ₹${proofLimit.toLocaleString("en-IN")} — attach bill proof`} data-testid="voucher-proof-missing-badge">
                Proof missing
              </span>
            ) : (
              "—"
            ),
        },
        { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) },
      ]}
      rowActions={(r) => (
        <>
          <VoucherProofActions row={r} />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary"
            title="Print voucher"
            onClick={() => printDebitVoucher(r)}
            data-testid="print-voucher-btn"
          >
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="Send voucher to admin on WhatsApp"
            onClick={() => voucherWhatsApp(r)}
            data-testid="whatsapp-voucher-btn"
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
        </>
      )}
      onSaved={(v) => {
        refreshRows();
        toast.success(`Voucher ${v.voucher_no || ""} saved`, {
          description: "Do you want to print it now?",
          duration: 8000,
          action: {
            label: "Print",
            onClick: () => printDebitVoucher(v),
          },
        });
      }}
    />
    <BlankVoucherDialog open={blankOpen} onClose={() => setBlankOpen(false)} existingRows={allRows} />
    </div>
  );
}
